import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import RequiredMark from '@/shein-platform/components/RequiredMark';
import { parseAddedCustomAttributeValue } from '@/shein-platform/utils/sheinCustomAttribute';
import { sheinPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';

/**
 * 申请新增自定义属性值（平台 add-custom-attribute-value）。
 */
export default function CustomAttributeValueModal({
  open,
  shopId,
  categoryId,
  attributeId,
  attributeLabel = '属性',
  onClose,
  onAdded,
}) {
  useTranslation('workbench');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setValue('');
    setBusy(false);
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    const text = String(value || '').trim();
    if (!text) {
      showToast(chartText('publishExtra.fillValue', '请填写属性值'), 'error');
      return;
    }
    if (text.length > 100) {
      showToast(chartText('publishExtra.valueMax100', '属性值最多 100 个字符'), 'error');
      return;
    }
    if (!shopId || !categoryId || !attributeId) {
      showToast(chartText('publishExtra.missingMeta', '缺少店铺/类目/属性信息，无法申请'), 'error');
      return;
    }
    setBusy(true);
    try {
      const res = await sheinPlatformAPI.addCustomAttributeValue(shopId, {
        attribute_id: Number(attributeId),
        category_id: Number(categoryId),
        attribute_value: text,
      });
      const opt = parseAddedCustomAttributeValue(res) || {
        id: String(res?.attribute_value_id ?? res?.attributeValueId ?? ''),
        name: text,
        nameCn: text,
      };
      if (!opt.id) {
        showToast(chartText('publishExtra.noValueId', '平台未返回属性值 ID，请稍后刷新属性后重试'), 'error');
        return;
      }
      showToast(chartText('publishExtra.valueAdded', '自定义属性值已添加'), 'success');
      onAdded?.(opt);
      onClose?.();
    } catch (e) {
      showToast(e?.message || chartText('publishExtra.addFail', '添加自定义属性值失败'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="spo-batch-modal spo-custom-attr-modal" role="presentation">
      <div className="spo-batch-modal__backdrop" aria-hidden="true" />
      <div
        className="spo-batch-modal__panel spo-custom-attr-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-custom-attr-title"
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-custom-attr-title">{chartText('publishExtra.applyTitle', fixedUi("申请新增属性值"))}</h2>
          <button type="button" className="spo-batch-modal__close" aria-label={chartText('moduleShell.close', fixedUi("关闭"))} disabled={busy} onClick={onClose}>×</button>
        </div>
        <div className="spo-batch-modal__body">
          <p className="spo-batch-modal__hint">
            {chartText('publishExtra.applyHint100', `为「${attributeLabel}」申请新增属性值（最多 100 字，请使用半角符号）`, { name: attributeLabel })}
          </p>
          <label className="spo-field spo-field--full">
            <span className="spo-field__label">{chartText('publishExtra.attrValue', fixedUi("属性值"))} <RequiredMark /></span>
            <input
              ref={inputRef}
              className="spo-input"
              value={value}
              disabled={busy}
              maxLength={100}
              placeholder={chartText('publishExtra.valuePh', '请输入自定义属性值')}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          </label>
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" disabled={busy} onClick={onClose}>
            {chartText('shop.cancel', '取消')}
          </button>
          <button type="button" className="spo-btn spo-btn--primary spo-btn--sm" disabled={busy} onClick={submit}>
            {busy ? chartText('returnList.submitting', fixedUi("提交中…")) : chartText('publishExtra.submitApply', '提交申请')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
